import RMCheckBox from 'components/customComponents/Controls/RMCheckbox';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { showSnackBar } from 'utils/Global';
import { OptionsRadioButton } from './SingleDropReportOptions';
import RMSelect from 'components/customComponents/Controls/RMSelect';
import { ImageReportTextInput } from './ImageReportOptions';
import { generateIngredientReportPDF } from './IngredientReportPdf';
import {
  IngredientReportOptionProps,
  IngredientReportOptionsDTO,
  fontFamilies,
  fontStyles,
} from './ReportsConstants';
import { useExperimentReady, useMaxIngredientsOfWell } from './hooks';
import {
  useAllWellNavigationDetails,
  useSelectedDropImageParameters,
} from 'redux/hooks/experiment';

export const IngredientsReportOptions = forwardRef(
  (props: IngredientReportOptionProps, ref) => {
    const dropNumber = 'drop1';
    const [fitToPage, setFitToPage] = useState(false);
    const [rowLimit, setRowLimit] = useState(4);
    const [columnLimit, setColumnLimit] = useState(6);
    const rowValueRef = useRef(null);
    const columnValueRef = useRef(null);
    const [ingredientNamePreference, setIngredientNamePreference] = useState('longName');
    const allWellNavigationDetails = useAllWellNavigationDetails();
    const selectedDropImageParameters = useSelectedDropImageParameters();
    const [fontFamily, setFontFamily] = useState(fontFamilies.sort()[0]);
    const [fontStyle, setFontStyle] = useState(fontStyles.sort()[0]);
    const [selectAllDrop, setSelectAllDrop] = useState(false);
    const [concentration, setConcentration] = useState<boolean>(true);
    const [pH, setpH] = useState<boolean>(true);
    const [pHVolumes, setpHVolumes] = useState<boolean>(true);
    const [showProteinName, setShowProteinName] = useState<boolean>(true);
    const [showDropVolumes, setShowDropVolumes] = useState<boolean>(true);
    const [showWellVolumes, setShowWellVolumes] = useState<boolean>(true);
    const [showColumnNameHeading, setShowColumnNameHeading] = useState<boolean>(true);
    const [maxIngredientsWell, setMaxIngredientsWell] = useState<number>(null);
    const [maxIngredients, setMaxIngredients] = useState<number>(null);
    const [drops, setDrops] = useState([]);
    const [isScreenExperiment, setIsScreenExperiment] = useState<boolean>(false);
    const populatingWellList = useExperimentReady(props.docData);
    useMaxIngredientsOfWell(props, setMaxIngredientsWell, setMaxIngredients);

    useEffect(() => {
      const dropsArr = [];
      if (props.docData.containerDrops) {
        props.docData.containerDrops.forEach((element) => {
          const drop = {};
          drop['name'] = 'drop' + element.dropNumber;
          drop['dropNumber'] = element.dropNumber;
          drop['checked'] = element.dropNumber == 1 ? true : false;
          dropsArr.push(drop);
        });
        setDrops(dropsArr);
      } else {
        setIsScreenExperiment(true);
        setShowDropVolumes(false);
        setShowProteinName(false);
      }
    }, [props.docData.containerDrops]);

    useEffect(() => {
      if (selectAllDrop) {
        const tempDrops = [...drops];
        tempDrops.forEach((drop) => {
          drop.checked = true;
        });
        setDrops(tempDrops);
      }
    }, [selectAllDrop]);

    useEffect(() => {
      if (fitToPage === true) {
        setRowLimit(props.docData.containerRows);
        setColumnLimit(props.docData.containerCols);
        rowValueRef.current.value = null;
        columnValueRef.current.value = null;
      } else {
        setRowLimit(4);
        setColumnLimit(6);
      }
    }, [fitToPage]);

    useImperativeHandle(ref, () => ({
      generateAction,
    }));

    const validateOptions = async () => {
      if (
        rowLimit < 1 ||
        rowLimit > props.docData.containerRows ||
        (rowLimit === null && fitToPage !== true) ||
        columnLimit < 1 ||
        columnLimit > props.docData.containerCols ||
        (columnLimit === null && fitToPage !== true)
      ) {
        showSnackBar('Please enter valid number of rows and columns.');
        return false;
      } else if (!concentration && !pH && !pHVolumes) {
        showSnackBar('Please select Ingredient Data Option.');
        return false;
      } else if (maxIngredients > maxIngredientsWell || maxIngredients == 0) {
        showSnackBar('Please select a valid value of max ingredients.');
        return false;
      } else {
        return true;
      }
    };

    async function generateAction(saveReport: boolean, onPopupBlocked?: () => void) {
      const completeDocData = await populatingWellList;
      const validationResult = await validateOptions();
      if (!validationResult) {
        return false;
      } else {
        const reportOptions: IngredientReportOptionsDTO = {
          dropNumber,
          fitToPage,
          rowLimit,
          columnLimit,
          maxIngredients,
          showProteinName,
          showColumnNameHeading,
          showDropVolumes,
          showWellVolumes,
          concentration,
          pH,
          pHVolumes,
          ingredientNamePreference,
          drops,
          fontFamily,
          fontStyle,
          isScreenExperiment,
        };

        await generateIngredientReportPDF(
          reportOptions,
          completeDocData,
          allWellNavigationDetails,
          selectedDropImageParameters?.dropNumber,
          saveReport,
          onPopupBlocked,
        );
        return true;
      }
    }

    function changeColumnValue(e) {
      if (!isNaN(Number(e.target.value))) {
        if (e.target.value === '') {
          setColumnLimit(null);
        } else {
          setColumnLimit(Number(e.target.value));
        }
      } else {
        setColumnLimit(null);
      }
    }
    function changeRowValue(e) {
      if (!isNaN(Number(e.target.value))) {
        if (e.target.value === '') {
          setRowLimit(null);
        } else {
          setRowLimit(Number(e.target.value));
        }
      } else {
        setRowLimit(null);
      }
    }

    function nameTypeSelectedValue(e) {
      const value = e.target.value;
      setIngredientNamePreference(value);
      return e;
    }

    function onChangeFontStyle(e) {
      const selectedStatusIndex = e.target.value;
      setFontStyle(selectedStatusIndex);
    }

    function onChangeFontFamily(e) {
      setFontFamily(fontFamilies.find((font) => font.key == e.target.value));
    }

    const onChangeConcentration = () => {
      setConcentration(!concentration);
    };

    const onChangepH = () => {
      setpH(!pH);
    };

    const onChangepHVolumes = () => {
      setpHVolumes(!pHVolumes);
    };
    useEffect(() => {
      if (drops.every((drop) => drop.checked == true) && selectAllDrop !== true) {
        setSelectAllDrop(true);
      } else if (
        !drops.every((drop) => drop.checked == true) &&
        selectAllDrop !== false
      ) {
        setSelectAllDrop(false);
      }
    }, [drops]);

    function onChangeHandler(event) {
      const tempDrops = [...drops];
      tempDrops.filter((x) => x['name'] === event.target.name)[0].checked =
        event.target.checked;
      setDrops(tempDrops);
    }

    const renderDropsList = useMemo(() => {
      return drops.map((drop) => (
        <>
          <div
            id={`ingredientsreportoptions-renderdroplist-wrapper-drop-${drop.dropNumber}`}
            className="flex flex-row align-items-center mt-[10px]"
          >
            <div id={`ingredientsreportoptions-checkbox-wrapper-drop-${drop.dropNumber}`}>
              <input
                id={`ingredientsreportoptions-checkbox-drop-${drop.dropNumber}`}
                type="checkbox"
                name={drop['name']}
                className="w-[18px] h-[18px]"
                onChange={onChangeHandler}
                checked={drop['checked']}
              />
            </div>
            <label
              id={`ingredientsreportoptions-label-drop-${drop.dropNumber}`}
              style={{ fontFamily: 'Roboto' }}
              className="flex-90 text-p non-select text-[14px] ml-[10px]"
            >
              Drop {drop['dropNumber']}
            </label>
          </div>
        </>
      ));
    }, [drops]);

    const onChangeSelectAllDrop = (e) => {
      setSelectAllDrop(e.target.checked);
      setDrops((prevDrops) => {
        const updatedDrops = [...drops];
        updatedDrops.forEach((drop) => (drop.checked = e.target.checked));
        return updatedDrops;
      });
    };

    return (
      <>
        <text
          id={`ingredientsreportoptions-text-message`}
          style={{ fontFamily: 'Roboto' }}
          className=" text-[17px] text-[#5D5D5D]"
        >
          Please provide information you want to include into report.
        </text>
        <div id="ingredientsreportoptions-wrapper" className="flex flex-row mt-[10px]">
          <div
            id="ingredientsreportoptions-basic-setting-wrapper"
            className=" w-[35%] flex flex-col gap-[16px]"
            style={{ borderRight: '2px solid #CACACA' }}
          >
            <div
              id="ingredientsreportoptions-basic-drop-lists-section"
              className="flex flex-col"
            >
              <div
                id="ingredientsreportoptions-basic-drop-lists-select-all-setting"
                className="flex flex-row align-items-center "
              >
                <div id="ingredientsreportoptions-basic-drop-lists-select-all-checkbox">
                  <RMCheckBox
                    style={{ borderRadius: '4px' }}
                    onChange={(e) => {
                      onChangeSelectAllDrop(e);
                    }}
                    isChecked={selectAllDrop}
                    isDisabled={isScreenExperiment ? true : false}
                    className={'w-[18px] h-[18px]'}
                  />
                </div>
                <label
                  id="ingredientsreportoptions-basic-drop-lists-select-all-text"
                  style={{ fontFamily: 'Roboto' }}
                  className="flex-90 text-p non-select text-[14px] ml-[10px]"
                >
                  {' '}
                  Select All Drop{' '}
                </label>
              </div>
              <div
                id="ingredientsreportoptions-basic-drop-lists-render-section"
                className="grid grid-cols-3 ml-[25px]"
              >
                {renderDropsList}
              </div>
            </div>

            <div
              id="ingredientsreportoptions-basic-max-ingredient-setting"
              className="flex flex-row"
            >
              <text
                id="ingredientsreportoptions-basic-max-ingredient-text"
                style={{ fontFamily: 'Roboto' }}
                className="text-[14px] text-[#1B1B1B]"
              >
                Max Ingredient to show
              </text>
              <ImageReportTextInput
                ref={rowValueRef}
                placeHolder={''}
                onChange={(e) => {
                  setMaxIngredients(e.target.value);
                }}
                value={maxIngredients}
                disabled={false}
                inputClass={
                  'w-[80px] rounded-[2px] border-solid border-[#C2C2C2] ml-[18px]'
                }
              />
            </div>

            <div
              id="ingredientsreportoptions-basic-show-protein-name-setting"
              className="flex flex-row align-items-center"
            >
              <div id="ingredientsreportoptions-basic-show-protein-name-checkbox">
                <RMCheckBox
                  style={{ borderRadius: '4px' }}
                  onChange={() => {
                    setShowProteinName(!showProteinName);
                  }}
                  isChecked={showProteinName}
                  isDisabled={isScreenExperiment ? true : false}
                  className={'w-[18px] h-[18px]'}
                />
              </div>
              <label
                id="ingredientsreportoptions-basic-show-protein-name-text"
                style={{ fontFamily: 'Roboto' }}
                className="flex-90 text-p non-select text-[14px] ml-[10px]"
              >
                {' '}
                Show Protein Name{' '}
              </label>
            </div>

            <div
              id="ingredientsreportoptions-basic-show-drop-volumes-setting"
              className="flex flex-row align-items-center"
            >
              <div id="ingredientsreportoptions-basic-show-drop-volumes-checkbox">
                <RMCheckBox
                  style={{ borderRadius: '4px' }}
                  onChange={() => {
                    setShowDropVolumes(!showDropVolumes);
                  }}
                  isChecked={showDropVolumes}
                  isDisabled={isScreenExperiment ? true : false}
                  className={'w-[18px] h-[18px]'}
                />
              </div>
              <label
                id="ingredientsreportoptions-basic-show-drop-volumes-text"
                style={{ fontFamily: 'Roboto' }}
                className="flex-90 text-p non-select text-[14px] ml-[10px]"
              >
                Show Drop Volumes{' '}
              </label>
            </div>

            <div
              id="ingredientsreportoptions-basic-show-well-volumes-setting"
              className="flex flex-row align-items-center"
            >
              <div id="ingredientsreportoptions-basic-show-well-volumes-checkbox">
                <RMCheckBox
                  style={{ borderRadius: '4px' }}
                  onChange={() => {
                    setShowWellVolumes(!showWellVolumes);
                  }}
                  isChecked={showWellVolumes}
                  isDisabled={false}
                  className={'w-[18px] h-[18px]'}
                />
              </div>
              <label
                id="ingredientsreportoptions-basic-show-well-volumes-text"
                style={{ fontFamily: 'Roboto' }}
                className="flex-90 text-p non-select text-[14px] ml-[10px]"
              >
                Show Well Volumes{' '}
              </label>
            </div>

            <div
              id="ingredientsreportoptions-basic-show-column-name-setting"
              className="flex flex-row align-items-center"
            >
              <div id="ingredientsreportoptions-basic-show-column-name-checkbox">
                <RMCheckBox
                  style={{ borderRadius: '4px' }}
                  onChange={() => {
                    setShowColumnNameHeading(!showColumnNameHeading);
                  }}
                  isChecked={showColumnNameHeading}
                  isDisabled={false}
                  className={'w-[18px] h-[18px]'}
                />
              </div>
              <label
                id="ingredientsreportoptions-basic-show-column-name-text"
                style={{ fontFamily: 'Roboto' }}
                className="flex-90 text-p non-select text-[14px] ml-[10px]"
              >
                Show Column Name Heading{' '}
              </label>
            </div>
            <div
              id="ingredientsreportoptions-basic-fit-to-page-setting"
              className="flex flex-row align-items-center"
            >
              <div id="ingredientsreportoptions-basic-fit-to-page-checkbox">
                <RMCheckBox
                  style={{ borderRadius: '4px' }}
                  onChange={(e) => {
                    setFitToPage(e.target.checked);
                  }}
                  isChecked={fitToPage}
                  className={'w-[18px] h-[18px]'}
                />
              </div>
              <label
                id="ingredientsreportoptions-basic-fit-to-page-text"
                style={{ fontFamily: 'Roboto' }}
                className="flex-90 text-p non-select text-[14px] ml-[10px]"
              >
                Fit to Page{' '}
              </label>
            </div>

            <div
              id="ingredientsreportoptions-row-column-setting"
              className="flex flex-row ml-[22px] align-middle"
            >
              <div id="ingredientsreportoptions-row-setting" className="flex flex-row">
                <label
                  id="ingredientsreportoptions-row-text"
                  style={{ fontFamily: 'Roboto' }}
                  className="flex-90 text-p non-select text-[14px]"
                >
                  Row{' '}
                </label>
                <ImageReportTextInput
                  ref={rowValueRef}
                  placeHolder={''}
                  onChange={changeRowValue}
                  value={rowLimit}
                  disabled={fitToPage}
                  inputClass={
                    'w-[50px] rounded-[2px] border-solid border-[#C2C2C2] ml-[10px]'
                  }
                />
              </div>

              <div
                id="ingredientsreportoptions-column-setting"
                className="flex flex-row ml-[18px]"
              >
                <label
                  id="ingredientsreportoptions-column-text"
                  style={{ fontFamily: 'Roboto' }}
                  className="flex-90 text-p non-select text-[14px]"
                >
                  Column{' '}
                </label>
                <ImageReportTextInput
                  ref={columnValueRef}
                  placeHolder={''}
                  onChange={changeColumnValue}
                  value={columnLimit}
                  disabled={fitToPage}
                  inputClass={
                    'w-[50px] rounded-[2px] border-solid border-[#C2C2C2] ml-[10px]'
                  }
                />
              </div>
            </div>
          </div>
          <div
            id="ingredientsreportoptions-ingredient-setting-wrapper"
            className=" w-[30%] flex flex-col gap-[16px] ml-[15px]"
            style={{ borderRight: '2px solid #CACACA' }}
          >
            <text
              id="ingredientsreportoptions-ingredientnamepreference-text"
              style={{ color: '#1B1B1B' }}
              className="text-[14px]"
            >
              Ingredient Name Preferences
            </text>
            <OptionsRadioButton
              id="ingredientsreportoptions-ingredientnamepreference-longname-option"
              onClick={nameTypeSelectedValue}
              value="longName"
              label="Long Ingredient Name"
              selected={ingredientNamePreference}
            />
            <OptionsRadioButton
              id="ingredientsreportoptions-ingredientnamepreference-shortname-option"
              onClick={nameTypeSelectedValue}
              value="shortName"
              label="Short Ingredient Name"
              selected={ingredientNamePreference}
            />
            <text
              id="ingredientsreportoptions-ingredientdataoptions-text"
              className=" text-[14px]"
              style={{ fontFamily: 'Roboto' }}
            >
              Ingredient Data Options
            </text>
            <div
              id="ingredientsreportoptions-ingredientdataoptions-setting"
              className="flex flex-row align-items-center"
            >
              <div id="ingredientsreportoptions-ingredientdataoptions-concentration-checkbox">
                <RMCheckBox
                  style={{ borderRadius: '4px' }}
                  onChange={onChangeConcentration}
                  isChecked={concentration}
                  isDisabled={false}
                  className={'w-[18px] h-[18px]'}
                />
              </div>
              <label
                id="ingredientsreportoptions-ingredientdataoptions-concentration-text"
                style={{ fontFamily: 'Roboto' }}
                className="flex-90 text-p non-select text-[14px] ml-[10px]"
              >
                {' '}
                Concentration{' '}
              </label>
            </div>

            <div
              id="ingredientsreportoptions-ingredientdataoptions-ph-setting"
              className="flex flex-row align-items-center"
            >
              <div id="ingredientsreportoptions-ingredientdataoptions-ph-checkbox">
                <RMCheckBox
                  style={{ borderRadius: '4px' }}
                  onChange={onChangepH}
                  isChecked={pH}
                  isDisabled={false}
                  className={'w-[18px] h-[18px]'}
                />
              </div>
              <label
                id="ingredientsreportoptions-ingredientdataoptions-ph-text"
                style={{ fontFamily: 'Roboto' }}
                className="flex-90 text-p non-select text-[14px] ml-[10px]"
              >
                {' '}
                pH{' '}
              </label>
            </div>

            <div
              id="ingredientsreportoptions-ingredientdataoptions-ph-volumes-setting"
              className="flex flex-row align-items-center"
            >
              <div id="ingredientsreportoptions-ingredientdataoptions-ph-volumes-checkbox">
                <RMCheckBox
                  style={{ borderRadius: '4px' }}
                  onChange={onChangepHVolumes}
                  isChecked={pHVolumes}
                  isDisabled={false}
                  className={'w-[18px] h-[18px]'}
                />
              </div>
              <label
                id="ingredientsreportoptions-ingredientdataoptions-ph-volumes-text"
                style={{ fontFamily: 'Roboto' }}
                className="flex-90 text-p non-select text-[14px] ml-[10px]"
              >
                {' '}
                pH Volumes{' '}
              </label>
            </div>
          </div>

          <div
            id="ingredientsreportoptions-advancedfontsettings-section"
            className=" flex flex-col ml-[15px] gap-[16px] w-[250px]"
          >
            <text
              id="ingredientsreportoptions-advancedfontsettings-title"
              className="text-[14px] text-[#1B1B1B]"
            >
              Advanced Font Settings
            </text>
            <div id="ingredientsreportoptions-font-setting" className="flex flex-row">
              <label
                id="ingredientsreportoptions-font-text"
                style={{ fontFamily: 'Roboto' }}
                className="w-[33%] text-[14px]"
              >
                Font
              </label>
              <RMSelect
                style={{
                  fontFamily: 'Roboto',
                  height: '2rem',
                  width: '168px',
                  borderRadius: '2px',
                  borderColor: '#C2C2C2',
                  fontSize: '14px',
                }}
                options={fontFamilies.map((font) => {
                  return { id: font.key, name: font.name };
                })}
                selectedValue={fontFamily.key}
                onChange={(e) => onChangeFontFamily(e)}
                placeholder={'any'}
                disabled={false}
              />
            </div>

            <div
              id="ingredientsreportoptions-font-style-settings"
              className="flex flex-row"
            >
              <text
                id="ingredientsreportoptions-font-style-text"
                style={{ fontFamily: 'Roboto' }}
                className="w-[33%] text-[14px]"
              >
                Font Style
              </text>
              <RMSelect
                style={{
                  fontFamily: 'Roboto',
                  height: '2rem',
                  width: '168px',
                  borderRadius: '2px',
                  borderColor: '#C2C2C2',
                  fontSize: '14px',
                }}
                options={fontStyles.map((fontStyle) => {
                  return { id: fontStyle, name: fontStyle };
                })}
                selectedValue={fontStyle}
                onChange={(e) => onChangeFontStyle(e)}
                placeholder={'any'}
                disabled={false}
              />
            </div>

            <text
              id="ingredientsreportoptions-font-sample-text"
              style={{ fontFamily: 'Roboto' }}
              className=" text-[14px]"
            >
              Font Sample
            </text>
            <textarea
              id="ingredientsreportoptions-font-sample-text-area"
              className="text-[16px] border-[#999999] h-[100px] text-[#B4B4B4] pl-[12px] w-[100%]"
              onChange={() => {}}
              disabled={false}
              readOnly={true}
              style={{
                fontFamily: fontFamily.name,
                fontWeight:
                  fontStyle == 'Bold' || fontStyle == 'Bold Italic' ? 'bold' : 'normal',
                fontStyle:
                  fontStyle == 'Italic' || fontStyle == 'Bold Italic'
                    ? 'italic'
                    : 'normal',
              }}
            >
              Sample Text
            </textarea>
          </div>
        </div>
      </>
    );
  },
);
